import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from '../../../services/analytics.service';
import { ParametrosService } from '../../../services/parametros.service';
import { RelatorioVendasDetalhado, RelatorioVendasItem } from '../../../models/financeiro.model';
import { formatarMoedaBRL } from '../../../core/utils/moeda.util';
import { Parametros } from '../../../models/parametros.model';

@Component({
  selector: 'app-relatorio-vendas',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './relatorio-vendas.component.html',
  styleUrl: './relatorio-vendas.component.css'
})
export class RelatorioVendasComponent implements OnInit {
  private analyticsService = inject(AnalyticsService);
  private parametrosService = inject(ParametrosService);
  private toastr = inject(ToastrService);
  private datePipe = inject(DatePipe);

  statusVenda = '';
  formaPagamento = '';
  idVendedor: number | null = null;
  dataInicial = '';
  dataFinal = '';

  relatorio: RelatorioVendasDetalhado | null = null;
  carregado = false;
  carregando = false;
  tentouBuscar = false;
  dataGeracao = '';

  formasPagamento: Parametros[] = [];
  statusOptions: Parametros[] = [];

  ngOnInit(): void {
    this.loadParametros();
  }

  loadParametros(): void {
    this.parametrosService.getParametrosByCategorias(['FORMA_PAGAMENTO', 'STATUS_VENDA']).subscribe({
      next: (data) => {
        this.formasPagamento = data.filter(p => p.categoria === 'FORMA_PAGAMENTO');
        this.statusOptions = data.filter(p => p.categoria === 'STATUS_VENDA');
      },
      error: () => this.toastr.error('Erro ao carregar os parâmetros de filtro.', 'Erro')
    });
  }

  get vendas(): RelatorioVendasItem[] {
    return this.relatorio?.itens ?? [];
  }

  get quantidadeRegistros(): number {
    return this.relatorio?.quantidadeRegistros ?? this.vendas.length;
  }

  get valorTotalPeriodo(): number {
    return this.relatorio?.valorTotalPeriodo ?? 0;
  }

  get dataInicialInvalida(): boolean {
    return this.tentouBuscar && this.dataInicial.trim() === '';
  }

  get dataFinalInvalida(): boolean {
    return this.tentouBuscar && this.dataFinal.trim() === '';
  }

  buscar(): void {
    this.tentouBuscar = true;

    if (this.dataInicial.trim() === '' || this.dataFinal.trim() === '') {
      this.toastr.warning('Informe a data inicial e a data final para gerar o relatório.', 'Período obrigatório');
      return;
    }

    this.carregando = true;
    this.carregado = false;

    const idVendedor = this.idVendedor ?? undefined;
    const status = this.statusVenda.trim() !== '' ? this.statusVenda : undefined;
    const formaPag = this.formaPagamento.trim() !== '' ? this.formaPagamento : undefined;

    this.analyticsService.getRelatorioVendasDetalhado(this.dataInicial, this.dataFinal, idVendedor, status, formaPag).subscribe({
      next: (data) => {
        this.relatorio = data;
        this.dataGeracao = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') ?? '';
        this.carregado = true;
        this.carregando = false;
      },
      error: () => {
        this.toastr.error('Erro ao carregar relatório de vendas.', 'Erro');
        this.carregando = false;
      }
    });
  }

  imprimir(): void {
    const esc = (value: string | number | null | undefined): string =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const brl = (value: number) => (value != null ? formatarMoedaBRL(value) : 'R$ 0,00');

    const periodLabel = `${this.formatDate(this.dataInicial)} a ${this.formatDate(this.dataFinal)}`;

    const statusLabel = this.statusVenda || 'Todos';
    const formaPagamentoLabel = this.formaPagamento || 'Todas';
    const vendedorLabel = this.idVendedor ? `${this.idVendedor}` : 'Todos';

    const rows = this.vendas.map((venda, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${esc(venda.idVenda)}</td>
        <td>${esc(venda.idOrcamento)}</td>
        <td>${esc(venda.nomeCliente)}</td>
        <td>${esc(venda.nomeVendedor)}</td>
        <td class="center">${esc(this.formatDate(venda.dataVenda))}</td>
        <td class="center"><span class="status status-${esc((venda.statusVenda ?? '').toLowerCase())}">${esc(venda.statusVenda)}</span></td>
        <td class="center">${esc(venda.formaPagamento)}</td>
        <td class="right bold">${brl(venda.totalVenda)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Relatório de Vendas</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; background: #fff; }

    @page {
      size: A4 landscape;
      margin: 14mm 12mm 18mm 12mm;
      @bottom-left   { content: "Relatório de Vendas"; font-size: 7.5pt; color: #555; }
      @bottom-center { content: "Página " counter(page) " de " counter(pages); font-size: 7.5pt; color: #000; }
      @bottom-right  { content: "Gerado em: ${esc(this.dataGeracao)}"; font-size: 7.5pt; color: #555; }
    }

    /* Cabeçalho */
    .header { text-align: center; margin-bottom: 10px; }
    .header h1 { font-size: 16pt; font-weight: 700; letter-spacing: 0.5px; }
    .header .meta { font-size: 9pt; color: #444; margin-top: 4px; }
    .header .divider { border: none; border-top: 2px solid #000; margin-top: 8px; }

    /* Tabela */
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    thead tr { background-color: #1a1a2e; color: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    thead th { padding: 6px 7px; font-size: 9pt; font-weight: 700; text-align: left; border: 1px solid #333; }
    tbody td { padding: 4px 7px; font-size: 9pt; border: 1px solid #ccc; vertical-align: middle; }
    tbody tr.even { background-color: #f5f5f5; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    tbody tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }

    /* Totais */
    tfoot tr td { padding: 5px 7px; font-size: 9pt; font-weight: 700; border-top: 2px solid #000; border-bottom: 1px solid #ccc; background-color: #e0e0e0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* Alinhamentos */
    .center { text-align: center; }
    .right  { text-align: right; }
    .bold   { font-weight: 700; }
    thead th.right  { text-align: right; }
    thead th.center { text-align: center; }

    /* Status */
    .status { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 8pt; font-weight: 600;
               border: 1px solid #555; }
    .status-concluido { background-color: #d1e7dd; color: #0a3622; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .status-pago      { background-color: #d1e7dd; color: #0a3622; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .status-parcelado { background-color: #fff3cd; color: #4a3800; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .status-pendente  { background-color: #f8d7da; color: #58151c; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* Rodapé de resumo */
    .summary { margin-top: 14px; font-size: 9pt; color: #333; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Relatório de Vendas</h1>
    <div class="meta">
      Período: ${esc(periodLabel)} &nbsp;|&nbsp; Status: ${esc(statusLabel)} &nbsp;|&nbsp; Forma Pgto: ${esc(formaPagamentoLabel)} &nbsp;|&nbsp; Vendedor: ${esc(vendedorLabel)} &nbsp;|&nbsp; Total: ${esc(this.quantidadeRegistros)} registro(s) &nbsp;|&nbsp; Gerado em: ${esc(this.dataGeracao)}
    </div>
    <hr class="divider"/>
  </div>

  <table>
    <thead>
      <tr>
        <th>ID Venda</th>
        <th>ID Orçamento</th>
        <th>Cliente</th>
        <th>Vendedor</th>
        <th class="center">Data Venda</th>
        <th class="center">Status</th>
        <th class="center">Forma Pagamento</th>
        <th class="right">Total Venda</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td colspan="7" class="right">Total do período:</td>
        <td class="right">${brl(this.valorTotalPeriodo)}</td>
      </tr>
    </tfoot>
  </table>

  <div class="summary">
    Quantidade de vendas: <strong>${esc(this.quantidadeRegistros)}</strong> &nbsp;&nbsp; Valor total do período: <strong>${brl(this.valorTotalPeriodo)}</strong>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=1100,height=750');
    if (!win) {
      this.toastr.warning('O navegador bloqueou a abertura da janela de impressão. Permita pop-ups e tente novamente.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  }

  private formatDate(value: string | undefined): string {
    if (!value) return '';
    return this.datePipe.transform(value, 'dd/MM/yyyy') ?? value;
  }

  limpar(): void {
    this.statusVenda = '';
    this.formaPagamento = '';
    this.idVendedor = null;
    this.dataInicial = '';
    this.dataFinal = '';
    this.relatorio = null;
    this.carregado = false;
    this.tentouBuscar = false;
  }
}
